import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Field, Label } from '@/components/ui/catalyst/fieldset';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  FirmTaskTemplateSchema,
  useCreateFirmTaskTemplateMutation,
  useGetFirmTaskTemplateOptionsQuery,
  useUpdateFirmTaskTemplateMutation,
} from '@/services/api/firmTaskTemplatesService';
import { getApiErrorMessage } from '@/utils/api-error';

interface TaskTemplateEditorDialogProps {
  firmId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template?: FirmTaskTemplateSchema | null;
  onSuccess?: () => void;
}

export const TaskTemplateEditorDialog = ({
  firmId,
  open,
  onOpenChange,
  template,
  onSuccess,
}: TaskTemplateEditorDialogProps) => {
  const isEditing = !!template;

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [taskType, setTaskType] = useState('');
  const [assigneeRole, setAssigneeRole] = useState<string>('');
  const [slaPolicy, setSlaPolicy] = useState<string>('');

  // API hooks
  const { data: options, isLoading: isLoadingOptions } =
    useGetFirmTaskTemplateOptionsQuery(firmId, { skip: !open });

  const [createTemplate, { isLoading: isCreating }] =
    useCreateFirmTaskTemplateMutation();

  const [updateTemplate, { isLoading: isUpdating }] =
    useUpdateFirmTaskTemplateMutation();

  const isLoading = isCreating || isUpdating;

  // Initialize form when template changes
  useEffect(() => {
    if (template) {
      setName(template.name);
      setDescription(template.description);
      setTaskType(template.task_type);
      setAssigneeRole(template.assignee_role || '');
      setSlaPolicy(template.sla_resolution_policy || '');
    } else {
      setName('');
      setDescription('');
      setTaskType('');
      setAssigneeRole('');
      setSlaPolicy('');
    }
  }, [template, open]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }

    if (!taskType) {
      toast.error('Task type is required');
      return;
    }

    if (!assigneeRole) {
      toast.error('Assignee role is required');
      return;
    }

    if (!slaPolicy) {
      toast.error('SLA policy is required');
      return;
    }

    try {
      if (isEditing && template) {
        await updateTemplate({
          firmId,
          templateId: template.id,
          data: {
            name: name.trim(),
            description: description.trim(),
            task_type: taskType,
            assignee_role: assigneeRole || null,
            sla_resolution_policy: slaPolicy || null,
          },
        }).unwrap();
        toast.success('Firm task template updated');
      } else {
        await createTemplate({
          firmId,
          data: {
            name: name.trim(),
            description: description.trim(),
            task_type: taskType,
            assignee_role: assigneeRole || null,
            sla_resolution_policy: slaPolicy || null,
            status: 'auto_create',
          },
        }).unwrap();
        toast.success('Firm task template created');
      }

      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      const fallbackMessage = isEditing
        ? 'Failed to update task template'
        : 'Failed to create task template';
      toast.error(getApiErrorMessage(error, fallbackMessage));
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isEditing
              ? 'Edit Firm Task Template'
              : 'Create Firm Task Template'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the configuration for this firm-wide task.'
              : 'When enabled, all new matters will get this task created with the configuration below. Use this to create firm-specific tasks outside the typical Finch flow. Questions? Ask on #app-feedback.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Field>
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Do Client Homework"
            />
          </Field>

          <Field>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this task is for"
              rows={3}
            />
          </Field>

          <Field>
            <Label htmlFor="task_type">Task Type *</Label>
            <Select value={taskType} onValueChange={setTaskType}>
              <SelectTrigger id="task_type">
                <SelectValue
                  placeholder={
                    isLoadingOptions ? 'Loading...' : 'Select a task type'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {options?.task_types.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field>
            <Label htmlFor="assignee_role">Assignee Role *</Label>
            <Select value={assigneeRole} onValueChange={setAssigneeRole}>
              <SelectTrigger id="assignee_role">
                <SelectValue
                  placeholder={
                    isLoadingOptions ? 'Loading...' : 'Select an assignee role'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {options?.assignee_roles.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field>
            <Label htmlFor="sla_policy">SLA Policy *</Label>
            <Select value={slaPolicy} onValueChange={setSlaPolicy}>
              <SelectTrigger id="sla_policy">
                <SelectValue
                  placeholder={
                    isLoadingOptions ? 'Loading...' : 'Select an SLA policy'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {options?.sla_policies.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={handleCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={
              isLoading ||
              !name.trim() ||
              !taskType ||
              !assigneeRole ||
              !slaPolicy
            }
          >
            {isLoading && 'Saving...'}
            {!isLoading && isEditing && 'Save Changes'}
            {!isLoading && !isEditing && 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
